import { DashboardApi } from '@/apis';
import { action, flow, makeObservable, observable } from 'mobx';
import RootStore from '.';
import BaseStore from './BaseStore';
import { TableState } from '@tanstack/react-table';
import { ResponseData } from '@/types/http';
import { DASHBOARD_LIST_TYPE, DashboardListType } from '@/types/enums';
import { AddLatePayment, Dashboard, LatePaymentOrder, LateReturnedBike, Statistic, WaitingPaymentOrder, VehicleOverdueForArrangement, VehicleToArrangeIn24h } from '@/types/dashboard';

class DashboardStore extends BaseStore {
    defaultPagingCustom: TableState = {
        pagination: {
            pageIndex: 0,
            pageSize: 5
        },
        sorting: [{}]
    } as TableState;

    api: DashboardApi;

    dashboard: Dashboard = {} as Dashboard;
    dashboardListType: DashboardListType =
        DASHBOARD_LIST_TYPE.NeedingArranged24AndOverdue;

    // Vehicles to arrange in 24h
    vehiclesToArrangeIn24h: VehicleToArrangeIn24h[] = [];
    totalVehiclesToArrangeIn24h: number = 0;
    pagingVehiclesToArrangeIn24h: TableState = this.defaultPaging;

    statistics: Statistic = {} as Statistic;

    // Vehicles overdue for arrangement
    vehiclesOverdueForArrangement: VehicleOverdueForArrangement[] = [];
    totalVehiclesOverdueForArrangement: number = 0;
    pagingVehiclesOverdueForArrangement: TableState = this.defaultPaging;

    // Waiting Payment
    waitingPaymentOrders: WaitingPaymentOrder[] = [];
    totalWaitingPaymentOrders: number = 0;
    pagingWaitingPaymentOrders: TableState = this.defaultPagingCustom;
    // Late payment orders
    latePaymentOrders: LatePaymentOrder[] = [];
    totalLatePaymentOrders: number = 0;
    pagingLatePaymentOrders: TableState = this.defaultPagingCustom;
    // add late payment
    addLatePayments: AddLatePayment[] = [];
    totalAddLatePayments: number = 0;
    pagingAddLatePayments: TableState = this.defaultPagingCustom;

    // Late returned bikes
    lateReturnedBikes: LateReturnedBike[] = [];
    totalLateReturnedBikes: number = 0;
    pagingLateReturnedBikes: TableState = this.defaultPaging;

    constructor(rootStore: RootStore) {
        super(rootStore);
        makeObservable(this, {
            dashboardListType: observable,
            //
            dashboard: observable,
            getDashboards: flow.bound,
            statistics: observable,
            getStatistics: flow.bound,
            // Vehicles to arrange in 24h
            vehiclesToArrangeIn24h: observable,
            totalVehiclesToArrangeIn24h: observable,
            pagingVehiclesToArrangeIn24h: observable,
            getVehiclesToArrangeIn24h: flow.bound,
            cleanVehiclesToArrangeIn24h: action.bound,
            // Vehicles overdue for arrangement
            vehiclesOverdueForArrangement: observable,
            totalVehiclesOverdueForArrangement: observable,
            pagingVehiclesOverdueForArrangement: observable,
            getVehiclesOverdueForArrangement: flow.bound,
            cleanVehiclesOverdueForArrangement: action.bound,
            // Waiting Payment
            waitingPaymentOrders: observable,
            totalWaitingPaymentOrders: observable,
            pagingWaitingPaymentOrders: observable,
            getWaitingPayments: flow.bound,
            cleanWaitingPayments: action.bound,
            // Late payment orders
            latePaymentOrders: observable,
            totalLatePaymentOrders: observable,
            pagingLatePaymentOrders: observable,
            getLateReturnedBikes: flow.bound,
            cleanLatePaymentOrders: action.bound,
            // add late payment
            addLatePayments: observable,
            totalAddLatePayments: observable,
            pagingAddLatePayments: observable,
            getAddLatePayments: flow.bound,
            cleanAddLatePayments: action.bound,
            // Late returned bikes
            lateReturnedBikes: observable,
            totalLateReturnedBikes: observable,
            pagingLateReturnedBikes: observable,
            getLatePaymentOrders: flow.bound,
            cleanLateReturnedBikes: action.bound
        });
        this.api = new DashboardApi();
    }

    *getDashboards() {
        try {
            const res: ResponseData<Dashboard> = yield this.rootStore.apiStore.call(
                this.api,
                this.api.getDashboards
            );
            if (res.ok) {
                this.dashboard = res.data;
                return res.data;
            }
            return false;
        } catch (error) { }
    }

    // #region Vehicles to arrange in 24h
    *getVehiclesToArrangeIn24h(searchParams?: any, paging?: TableState) {
        try {
            const payload = {
                ...this.convertPagingFromTableToRequest(
                    paging ?? this.pagingVehiclesToArrangeIn24h
                ),
                ...searchParams
            };
            const res: ResponseData<any> = yield this.rootStore.apiStore.call(
                this.api,
                this.api.getVehiclesToArrangeIn24h,
                payload
            );
            if (res.ok) {
                this.vehiclesToArrangeIn24h = res.data?.elements;
                this.pagingVehiclesToArrangeIn24h = {
                    ...this.pagingVehiclesToArrangeIn24h,
                    sorting: paging?.sorting ?? [],
                    pagination: this.convertPaginationFromRequestToTable(
                        res.data.paginate
                    )
                };
                this.totalVehiclesToArrangeIn24h = res.data.paginate.totalRecord;
                return true;
            }
            return false;
        } catch (error) { }
    }

    *getStatistics() {
        try {
            const res: ResponseData<any> = yield this.rootStore.apiStore.call(
                this.api,
                this.api.getStatistics
            );
            if (res.ok) {
                this.statistics = res.data;
                return true;
            }
            return false;
        } catch (error) { }
    }
    // #endregion Vehicles to arrange in 24h

    // #region Vehicles overdue for arrangement
    *getVehiclesOverdueForArrangement(searchParams?: any, paging?: TableState) {
        try {
            const payload = {
                ...this.convertPagingFromTableToRequest(
                    paging ?? this.pagingVehiclesOverdueForArrangement
                ),
                ...searchParams
            };
            const res: ResponseData<any> = yield this.rootStore.apiStore.call(
                this.api,
                this.api.getVehiclesOverdueForArrangement,
                payload
            );
            if (res.ok) {
                this.vehiclesOverdueForArrangement = res.data?.elements;
                this.pagingVehiclesOverdueForArrangement = {
                    ...this.pagingVehiclesOverdueForArrangement,
                    sorting: paging?.sorting ?? [],
                    pagination: this.convertPaginationFromRequestToTable(
                        res.data.paginate
                    )
                };
                this.totalVehiclesOverdueForArrangement = res.data.paginate.totalRecord;
                return true;
            }
            return false;
        } catch (error) { }
    }
    // #endregion Vehicles overdue for arrangement

    // #region Unpaid Reservations
    *getWaitingPayments(searchParams?: any, paging?: TableState) {
        try {
            const payload = {
                ...this.convertPagingFromTableToRequest(
                    paging ?? this.pagingWaitingPaymentOrders
                ),
                ...searchParams
            };
            const res: ResponseData<any> = yield this.rootStore.apiStore.call(
                this.api,
                this.api.getUnpaidReservations,
                payload
            );
            if (res.ok) {
                this.waitingPaymentOrders = res.data?.elements;
                this.pagingWaitingPaymentOrders = {
                    ...this.pagingWaitingPaymentOrders,
                    sorting: paging?.sorting ?? [],
                    pagination: this.convertPaginationFromRequestToTable(
                        res.data.paginate
                    )
                };
                this.totalWaitingPaymentOrders = res.data.paginate.totalRecord;
                return true;
            }
            return false;
        } catch (error) { }
    }
    // #endregion Unpaid Reservations

    // #region Late payment orders
    *getLatePaymentOrders(searchParams?: any, paging?: TableState) {
        try {
            const payload = {
                ...this.convertPagingFromTableToRequest(
                    paging ?? this.pagingLatePaymentOrders
                ),
                ...searchParams
            };
            const res: ResponseData<any> = yield this.rootStore.apiStore.call(
                this.api,
                this.api.getLatePaymentOrders,
                payload
            );
            if (res.ok) {
                this.latePaymentOrders = res.data?.elements;
                this.pagingLatePaymentOrders = {
                    ...this.pagingLatePaymentOrders,
                    sorting: paging?.sorting ?? [],
                    pagination: this.convertPaginationFromRequestToTable(
                        res.data.paginate
                    )
                };
                this.totalLatePaymentOrders = res.data.paginate.totalRecord;
                return true;
            }
            return false;
        } catch (error) { }
    }
    // #endregion Late payment orders

    // #region Add late payment
    *getAddLatePayments(searchParams?: any, paging?: TableState) {
        try {
            const payload = {
                ...this.convertPagingFromTableToRequest(
                    paging ?? this.pagingAddLatePayments
                ),
                ...searchParams
            };
            const res: ResponseData<any> = yield this.rootStore.apiStore.call(
                this.api,
                this.api.getAddLatePayments,
                payload
            );
            if (res.ok) {
                this.addLatePayments = res.data?.elements;
                this.pagingAddLatePayments = {
                    ...this.pagingLatePaymentOrders,
                    sorting: paging?.sorting ?? [],
                    pagination: this.convertPaginationFromRequestToTable(
                        res.data.paginate
                    )
                };
                this.totalAddLatePayments = res.data.paginate.totalRecord;
                return true;
            }
            return false;
        } catch (error) { }
    }
    // #endregion Add late payment

    // #region Late returned bikes
    *getLateReturnedBikes(searchParams?: any, paging?: TableState) {
        try {
            const payload = {
                ...this.convertPagingFromTableToRequest(
                    paging ?? this.pagingLateReturnedBikes
                ),
                ...searchParams
            };
            const res: ResponseData<any> = yield this.rootStore.apiStore.call(
                this.api,
                this.api.getLateReturnedBikes,
                payload
            );
            if (res.ok) {
                this.lateReturnedBikes = res.data?.elements;
                this.pagingLateReturnedBikes = {
                    ...this.pagingLateReturnedBikes,
                    sorting: paging?.sorting ?? [],
                    pagination: this.convertPaginationFromRequestToTable(
                        res.data.paginate
                    )
                };
                this.totalLateReturnedBikes = res.data.paginate.totalRecord;
                return true;
            }
            return false;
        } catch (error) { }
    }
    // #endregion Late returned bikes

    clean() {
        super.clean();

        this.dashboard = {} as Dashboard;
        this.dashboardListType = DASHBOARD_LIST_TYPE.NeedingArranged24AndOverdue;

        // Vehicles to arrange in 24h
        this.vehiclesToArrangeIn24h = [];
        this.totalVehiclesToArrangeIn24h = 0;
        this.pagingVehiclesToArrangeIn24h = this.defaultPaging;
        this.statistics = {} as Statistic;

        // Vehicles overdue for arrangement
        this.vehiclesOverdueForArrangement = [];
        this.totalVehiclesOverdueForArrangement = 0;
        this.pagingVehiclesOverdueForArrangement = this.defaultPaging;

        // Late payment orders
        this.latePaymentOrders = [];
        this.totalLatePaymentOrders = 0;
        this.pagingLatePaymentOrders = this.defaultPagingCustom;
        // add late payment
        this.addLatePayments = [];
        this.totalAddLatePayments = 0;
        this.pagingAddLatePayments = this.defaultPagingCustom;

        // Late returned bikes
        this.lateReturnedBikes = [];
        this.totalLateReturnedBikes = 0;
        this.pagingLateReturnedBikes = this.defaultPaging;
    }

    cleanVehiclesToArrangeIn24h() {
        super.clean();

        this.vehiclesToArrangeIn24h = [];
        this.totalVehiclesToArrangeIn24h = 0;
        this.pagingVehiclesToArrangeIn24h = this.defaultPaging;
        this.statistics = {} as Statistic;
    }

    cleanVehiclesOverdueForArrangement() {
        super.clean();

        this.vehiclesOverdueForArrangement = [];
        this.totalVehiclesOverdueForArrangement = 0;
        this.pagingVehiclesOverdueForArrangement = this.defaultPaging;
    }

    cleanWaitingPayments() {
        super.clean();

        this.waitingPaymentOrders = [];
        this.totalWaitingPaymentOrders = 0;
        this.pagingWaitingPaymentOrders = this.defaultPagingCustom;
    }

    cleanLatePaymentOrders() {
        super.clean();

        this.latePaymentOrders = [];
        this.totalLatePaymentOrders = 0;
        this.pagingLatePaymentOrders = this.defaultPagingCustom;
    }

    cleanAddLatePayments() {
        super.clean();

        this.addLatePayments = [];
        this.totalAddLatePayments = 0;
        this.pagingAddLatePayments = this.defaultPagingCustom;
    }

    cleanLateReturnedBikes() {
        super.clean();

        this.lateReturnedBikes = [];
        this.totalLateReturnedBikes = 0;
        this.pagingLateReturnedBikes = this.defaultPaging;
    }
}

export default DashboardStore;
